import { zoneFromPosition } from "@shared/lib/intensity/intensity-estimator";
import { INTENSITY_MAX, isIntensityPosition, type IntensityPosition } from "@shared/types/intensity";
import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormField, FormItem, FormLabel, FormMessage } from "@bcc/components/ui/form";
import { IntensityConfidenceBadge, IntensityScaleV2, type IntensityComparisonCoffee } from "@bcc/components/ui/intensity-scale";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";

import type { ProductFormData } from "../schemas/product-schema";
import { NO_COMPARISON_COFFEE, resolveMostSimilarTo, setIntensitySectionTestId } from "./intensity-section.utils";

interface IntensitySectionProps {
  /** The roaster's other coffees, rendered as markers and as the most-similar-to options. */
  comparisonCoffees?: readonly IntensityComparisonCoffee[];
  disabled?: boolean;
  /**
   * Whether the current score is PIM-approved, as opposed to merely locked
   * for an unrelated reason (e.g. the New Bag Size flow can disable this
   * section while the score is still an in-flight Salesforce provisional
   * estimate). Governs only the "preliminary" notice's wording — `disabled`
   * alone can't tell the two apart.
   */
  isApproved?: boolean;
}

/**
 * Reads the position of the coffee named `value`, or null for the cleared
 * sentinel. Keyed by name rather than array index so a provider refetch that
 * reorders the list can't silently repoint a stored choice at a different
 * coffee.
 */
const coffeeAt = (coffees: readonly IntensityComparisonCoffee[], value: string): IntensityPosition | null => {
  if (value === NO_COMPARISON_COFFEE) return null;
  return coffees.find((coffee) => coffee.name === value)?.position ?? null;
};

/**
 * The intensity block of the product form: a most-similar-to comparison
 * against the roaster's own line-up, and the nine-position scale.
 *
 * The dropdown is the only control. Picking a coffee sets the position to that
 * coffee's score and records the pick as `closestMatchCoffee` (sent to
 * Salesforce as `Closest_Match_Coffee__c`); picking "no coffee" clears both.
 * The scale is display-only — it shows the imported score and the result of
 * the selection. The zone is always recomputed from the final position — it
 * is never entered directly.
 */
export function IntensitySection({ comparisonCoffees = [], disabled = false, isApproved = false }: Readonly<IntensitySectionProps>) {
  const { t } = useTranslation();
  const form = useFormContext<ProductFormData>();
  const { control, getValues, setValue } = form;

  const intensityPosition = useWatch({ control, name: "intensityPosition" });
  const intensityZone = useWatch({ control, name: "intensityZone" });

  const closestMatchCoffee = useWatch({ control, name: "closestMatchCoffee" });
  const selectedPosition = isIntensityPosition(intensityPosition) ? intensityPosition : null;
  const hasUsablePick = Boolean(closestMatchCoffee) && comparisonCoffees.some((coffee) => coffee.name === closestMatchCoffee);
  // A score with no usable pick (an imported one that never had a pick, or a
  // pick whose coffee was renamed or removed) shows the empty placeholder, not
  // the "No coffee selected" option. Radix fires no change when the value it
  // already holds is re-selected, so showing that option here would leave the
  // roaster unable to clear the orphaned score.
  const mostSimilarTo = resolveMostSimilarTo(hasUsablePick ? closestMatchCoffee : null, selectedPosition !== null);

  const applyPosition = React.useCallback(
    (position: IntensityPosition | null) => {
      // A form reset() re-emits every watched value *after* the effect that
      // triggered it returns, so a synchronous "is this programmatic?" flag
      // cannot suppress the echo. Comparing against the value already in the
      // form does: an echo is a no-op, and a roaster's manual block click
      // survives the next render.
      if (getValues("intensityPosition") === position) return;
      setValue("intensityPosition", position, { shouldDirty: true, shouldValidate: true });
    },
    [getValues, setValue]
  );

  const handleSimilarChange = React.useCallback(
    (value: string) => {
      // Recorded even when the position doesn't change (picking the coffee
      // that already matches an imported score still has to be saved).
      setValue("closestMatchCoffee", value === NO_COMPARISON_COFFEE ? null : value, { shouldDirty: true });
      applyPosition(coffeeAt(comparisonCoffees, value));
    },
    [applyPosition, comparisonCoffees, setValue]
  );

  // The zone is a pure function of the position, so it is recomputed here
  // rather than being something the roaster can set.
  React.useEffect(() => {
    const expected = isIntensityPosition(intensityPosition) ? zoneFromPosition(intensityPosition) : "";
    if (intensityZone === expected) return;
    // Revalidate: moving the position across a zone boundary validates the new
    // position while the form still holds the old zone, which raises a
    // cross-field mismatch error. Repairing the zone silently would leave that
    // stale error on screen until some unrelated full validation cleared it.
    setValue("intensityZone", expected, { shouldDirty: false, shouldValidate: true });
  }, [intensityPosition, intensityZone, setValue]);

  const approvedTagLabel = t("views.shopifyImportModal.productForm.helpers.confidenceApproved");
  const provisionalTagLabel = t("views.shopifyImportModal.productForm.helpers.confidencePending");

  const testId = setIntensitySectionTestId("most-similar-to");

  return (
    <div className="space-y-6" data-testid={setIntensitySectionTestId("root")}>
      <section className="space-y-3">
        {comparisonCoffees.length === 0 ? (
          // First import for this roaster: the heading/subheading only make
          // sense once there's a line-up to compare against, so a bare
          // one-line explanation replaces the whole comparison prompt.
          <p className="text-xs text-muted-foreground" data-testid={setIntensitySectionTestId("comparison-empty")}>
            {t("views.shopifyImportModal.productForm.helpers.comparisonEmpty")}
          </p>
        ) : (
          <>
            <h4 className="text-sm font-bold">{t("views.shopifyImportModal.productForm.helpers.comparisonHeading")}</h4>
            <p className="text-xs text-muted-foreground">{t("views.shopifyImportModal.productForm.helpers.comparisonSubheading")}</p>
            <div className="max-w-xs space-y-2">
              <FormLabel htmlFor={testId}>{t("views.shopifyImportModal.productForm.labels.mostSimilarTo")}</FormLabel>
              <Select value={mostSimilarTo} onValueChange={handleSimilarChange} disabled={disabled}>
                <SelectTrigger id={testId} disabled={disabled} testId={testId}>
                  <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.comparisonCoffee")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_COMPARISON_COFFEE}>{t("views.shopifyImportModal.productForm.helpers.comparisonNone")}</SelectItem>
                  {comparisonCoffees.map((coffee) => (
                    <SelectItem key={coffee.name} value={coffee.name}>
                      <span className="flex w-full items-center justify-between gap-2">
                        <span className="truncate">{coffee.name}</span>
                        <IntensityConfidenceBadge
                          confidence={coffee.confidence}
                          approvedLabel={approvedTagLabel}
                          provisionalLabel={provisionalTagLabel}
                          testId={setIntensitySectionTestId(`most-similar-to-badge-${coffee.name}`)}
                        />
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </section>

      <FormField
        control={control}
        name="intensityPosition"
        render={() => (
          <FormItem>
            <IntensityScaleV2
              label={t("views.shopifyImportModal.productForm.labels.intensityPosition")}
              value={selectedPosition}
              readOnly
              disabled={disabled}
              comparisonCoffees={comparisonCoffees}
              approvedLabel={t("views.shopifyImportModal.productForm.helpers.markerApproved")}
              provisionalLabel={t("views.shopifyImportModal.productForm.helpers.markerProvisional")}
              formatScore={(position) => t("views.shopifyImportModal.productForm.helpers.markerScore", { position, max: INTENSITY_MAX })}
              unsetLabel={t("views.shopifyImportModal.productForm.helpers.intensityNotSelected")}
              testId={setIntensitySectionTestId("scale")}
            />
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium" data-testid={setIntensitySectionTestId("selected-readout")}>
                {selectedPosition === null
                  ? t("views.shopifyImportModal.productForm.helpers.intensityNotSelected")
                  : t("views.shopifyImportModal.productForm.helpers.intensitySelected", {
                      position: selectedPosition,
                      max: INTENSITY_MAX,
                      zone: t(`views.shopifyImportModal.productForm.intensityZones.${zoneFromPosition(selectedPosition)}`),
                    })}
              </p>
            </div>
            {comparisonCoffees.length > 0 && !disabled && (
              <p className="text-xs text-muted-foreground" data-testid={setIntensitySectionTestId("scale-hint")}>
                {t("views.shopifyImportModal.productForm.helpers.intensityScaleHint")}
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      {!isApproved && (
        <p className="text-xs text-muted-foreground" data-testid={setIntensitySectionTestId("preliminary-notice")}>
          {t("views.shopifyImportModal.productForm.helpers.intensityPreliminary")}
        </p>
      )}
    </div>
  );
}
